import { compact } from "lodash";
import {
  Redirect,
  Route,
  Switch,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { AssessmentType } from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { isAdminVar } from "@cache/cache";
import Layout from "@components/layout/layout";
import Tabs from "@components/tabs/tabs";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import { getAssessmentTypeLabel } from "@helpers/constants";

import AdoptionReport from "./components/adoption-report";
import AssessmentReportPage from "./components/assessment-report-page";
import OneononeReport from "./components/oneonone-report";
import SurveyReportPage from "./components/survey-report-page";
import TrendReportPage from "./components/trend-report-page";

const Nav = () => {
  const label = useLabel();
  const isAdmin = isAdminVar();
  const tabs = compact([
    {
      label: `${label("review", {
        capitalize: true,
        pluralize: true,
      })}`,
      to: "/reporting/manager-report",
      value: "manager-report",
    },
    {
      label: `${getAssessmentTypeLabel(AssessmentType.Manager)} ${label(
        "review",
        { capitalize: true, pluralize: true },
      )}`,
      to: "/reporting/upward-report",
      value: "upward-report",
    },
    {
      label: "Surveys",
      to: "/reporting/survey-report",
      value: "survey-report",
    },
    {
      label: "Trends",
      to: "/reporting/trend-report",
      value: "trend-report",
    },
    isAdmin && {
      label: label("oneonone", { capitalize: true, pluralize: true }),
      to: "/reporting/oneonone",
      value: "oneonone",
    },
    {
      label: "Usage & engagement",
      to: "/reporting/adoption",
      value: "adoption",
    },
  ]);

  const { navId } = useParams<{ navId: string }>();
  const displayedTabId = navId || tabs[0].value;
  return <Tabs options={tabs} selectedValue={displayedTabId} />;
};

const Reporting = () => {
  useDocumentTitle("Reporting");
  const { path } = useRouteMatch();
  const isAdmin = isAdminVar();

  return (
    <Layout>
      <Layout.Header
        breadcrumbs={[
          {
            title: "Reporting",
            url: "/reporting",
          },
        ]}
      />
      <Layout.Container aria-label="Reporting">
        <main className="absolute inset-0">
          <div className="@container/layout-main mx-auto">
            <div className="z-30 px-8 pt-6 pb-4 sticky top-0 bg-content-background">
              <Switch>
                <Route exact path={[`${path}/:navId`, path]}>
                  <Nav />
                </Route>
              </Switch>
            </div>
            <Switch>
              <Route exact path={path}>
                <Redirect to={`${path}/manager-report`} />
              </Route>
              <Route exact path={`${path}/manager-report`}>
                <AssessmentReportPage
                  assessmentType={AssessmentType.Performance}
                />
              </Route>
              <Route exact path={`${path}/upward-report`}>
                <AssessmentReportPage assessmentType={AssessmentType.Manager} />
              </Route>
              <Route exact path={`${path}/survey-report`}>
                <SurveyReportPage />
              </Route>
              <Route exact path={`${path}/trend-report`}>
                <TrendReportPage />
              </Route>
              {isAdmin && (
                <Route exact path={`${path}/oneonone`}>
                  <OneononeReport />
                </Route>
              )}
              <Route exact path={`${path}/adoption`}>
                <AdoptionReport />
              </Route>
            </Switch>
          </div>
        </main>
      </Layout.Container>
    </Layout>
  );
};

export default Reporting;
